import { cn } from "@/lib/utils";
import type { PipelineCount } from "@ow/shared";

type NodeState = "empty-future" | "empty-past" | "filled" | "frontier";

// Node band height; the rail is pinned to this band's vertical centre so the line bisects every disc.
const BAND = "h-9"; // 2.25rem
const RAIL_Y = "top-[1.125rem]"; // half of BAND → the exact node centre
// The end dots sit this far from each edge; the rail runs edge-dot-centre to edge-dot-centre (never past
// them), and the small margin gives the outer labels room without a big empty gutter.
const INSET = "1.5rem";

/** A single stage node. Occupied stages show a counted disc; the frontier (deepest occupied stage)
 *  carries the brand accent so the yellow marks ONE thing — how far the pipeline has reached. Empty
 *  stages are a small dot sitting ON the rail (ringed by the card colour so the line reads as passing
 *  through it). The disc is opaque so it cleanly covers the rail behind it. */
function Node({ count, state, selected }: { count: number; state: NodeState; selected: boolean }) {
  if (state === "filled" || state === "frontier") {
    const isFrontier = state === "frontier";
    return (
      <div
        className={cn(
          "grid size-9 shrink-0 place-items-center rounded-full text-sm font-semibold tabular-nums shadow-sm transition-shadow",
          isFrontier
            ? "bg-[var(--brand-accent)] text-[var(--brand-accent-foreground)]"
            : "border-2 border-foreground/20 bg-card text-foreground",
          selected && "ring-2 ring-foreground ring-offset-2 ring-offset-card",
        )}
      >
        {count}
      </div>
    );
  }
  return (
    <span
      className={cn(
        "rounded-full ring-4 ring-card transition-all",
        selected ? "size-3.5 ring-2 ring-foreground ring-offset-2 ring-offset-card" : "size-2.5",
        state === "empty-past" ? "bg-foreground/30" : "border border-dashed border-muted-foreground/50 bg-card",
      )}
    />
  );
}

/** One stage marker (node + label), absolutely centred on its point along the rail so the discs are
 *  evenly distributed edge-to-edge and each one lands dead-centre on the line. */
function StageMarker({
  left,
  label,
  count,
  state,
  selected,
  onSelect,
}: {
  left: string;
  label: string;
  count: number;
  state: NodeState;
  selected: boolean;
  onSelect?: () => void;
}) {
  const cls = cn(
    "group absolute top-0 flex w-24 -translate-x-1/2 flex-col items-center gap-2.5 rounded-lg pb-2 transition-colors",
    selected ? "bg-accent" : onSelect && "hover:bg-accent/50",
    onSelect && "cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ring",
  );
  const inner = (
    <>
      <span className={cn("flex items-center", BAND)}>
        <Node count={count} state={state} selected={selected} />
      </span>
      <span
        className={cn(
          "w-full truncate text-center text-[11px] font-mono uppercase leading-tight tracking-wide transition-colors",
          selected ? "font-semibold text-foreground" : count > 0 ? "text-foreground" : "text-muted-foreground",
        )}
        title={label}
      >
        {label}
      </span>
    </>
  );
  if (!onSelect)
    return (
      <div className={cls} style={{ left }}>
        {inner}
      </div>
    );
  return (
    <button type="button" onClick={onSelect} aria-pressed={selected} className={cls} style={{ left }}>
      {inner}
    </button>
  );
}

/** The role's pipeline as a connected stage flow. ONE continuous rail runs from the FIRST dot's centre
 *  to the LAST dot's centre — the dots are its endpoints, it never overshoots them — with a solid
 *  "progress" fill up to the frontier (deepest occupied stage). Nodes are evenly distributed and the
 *  rail bisects each disc. When `onStageSelect` is passed a stage toggles a filter on the list below. */
export function PipelineTracker({
  stages,
  leadKey,
  selectedKey,
  onStageSelect,
}: {
  stages: PipelineCount[];
  leadKey: string | null;
  selectedKey?: string | null;
  onStageSelect?: (key: string | null) => void;
}) {
  const leadIdx = leadKey ? stages.findIndex((s) => s.key === leadKey) : -1;
  const n = stages.length;
  // Fraction of the rail (0 at the first dot, 1 at the last) where node i sits.
  const frac = (i: number) => (n <= 1 ? 0 : i / (n - 1));
  // x for a node = INSET + frac · (track width), where track width = 100% − 2·INSET.
  const xOf = (i: number) => `calc(${INSET} + ${frac(i)} * (100% - 2 * ${INSET}))`;

  return (
    <div className="relative h-[4.25rem]">
      {/* continuous track from first dot centre to last dot centre */}
      <div
        className={cn("pointer-events-none absolute h-[3px] -translate-y-1/2 rounded-full bg-border", RAIL_Y)}
        style={{ left: INSET, right: INSET }}
        aria-hidden
      />
      {leadIdx >= 0 ? (
        <div
          className={cn("pointer-events-none absolute h-[3px] -translate-y-1/2 rounded-full bg-foreground/40", RAIL_Y)}
          style={{ left: INSET, width: `calc(${frac(leadIdx)} * (100% - 2 * ${INSET}))` }}
          aria-hidden
        />
      ) : null}
      {stages.map((s, i) => {
        const state: NodeState =
          s.count > 0
            ? i === leadIdx
              ? "frontier"
              : "filled"
            : leadIdx >= 0 && i < leadIdx
              ? "empty-past"
              : "empty-future";
        return (
          <StageMarker
            key={s.key}
            left={xOf(i)}
            label={s.label}
            count={s.count}
            state={state}
            selected={selectedKey === s.key}
            onSelect={onStageSelect ? () => onStageSelect(selectedKey === s.key ? null : s.key) : undefined}
          />
        );
      })}
    </div>
  );
}
